import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Phone, Save, User } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import OwnerSidebar from "@/components/navigation/OwnerSidebar";
import AppFooter from "@/components/layout/AppFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getDefaultHomePath, isPlanAdminUser } from "@/lib/roles";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const UserSettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout, updateCurrentUser } = useAuth();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dienstplaene, setDienstpläne] = useState([]);

  const homePath = useMemo(() => getDefaultHomePath(user), [user]);
  const showSidebar = isPlanAdminUser(user);

  useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
  }, [user?.name, user?.phone]);

  useEffect(() => {
    const fetchDienstpläne = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dienstplan`);
        setDienstpläne(response.data || []);
      } catch (error) {
        console.error("Error fetching dienstplaene:", error);
      }
    };
    fetchDienstpläne();
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Erfolgreich abgemeldet");
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    const nextName = (name || "").trim();
    const nextPhone = (phone || "").trim();
    const currentName = (user?.name || "").trim();
    const currentPhone = (user?.phone || "").trim();

    if (!nextName) {
      toast.error("Name darf nicht leer sein");
      return;
    }

    if (nextName === currentName && nextPhone === currentPhone) {
      toast.error("Keine Aenderungen vorhanden");
      return;
    }

    setSavingProfile(true);
    try {
      const response = await axios.put(`${API_URL}/api/auth/me`, {
        name: nextName,
        phone: nextPhone || null,
      });
      updateCurrentUser(response.data);
      setName(response.data?.name || "");
      setPhone(response.data?.phone || "");
      toast.success("Profildaten gespeichert");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Bitte alle Passwort-Felder ausfüllen");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Neues Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Neues Passwort und Wiederholung stimmen nicht überein");
      return;
    }

    setSavingPassword(true);
    try {
      const response = await axios.put(`${API_URL}/api/auth/me`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      updateCurrentUser(response.data);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Passwort geändert");
    } catch (error) {
      console.error("Error updating password:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Ändern des Passworts");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleOpenPlanManagement = () => {
    navigate("/dienstplan?create=1");
  };

  return (
    <div className="dashboard-layout" data-testid="user-settings-page">
      {showSidebar ? (
        <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />
      ) : null}

      {showSidebar ? (
        <OwnerSidebar
          user={user}
          sidebarOpen={sidebarOpen}
          activeMain="profile"
          plans={dienstplaene}
          onLogout={handleLogout}
        />
      ) : null}

      <main className="dashboard-main">
        <div className="max-w-5xl mx-auto px-6 py-8 flex-1 w-full space-y-6">
          <div className="flex items-center">
            <Button
              type="button"
              variant="outline"
              className="border-gray-300"
              onClick={() => navigate(homePath)}
              data-testid="profile-back-btn"
            >
              Zurück
            </Button>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-[#212529]">Benutzer Einstellungen</h1>
          <p className="text-gray-500">Name, Telefonnummer und Passwort verwalten.</p>
          {!isPlanAdminUser(user) && (
            <Card className="border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-[#212529]">Mehr Funktionen</CardTitle>
                <CardDescription className="text-gray-500">
                  Erstelle einen eigenen Dienstplan und werde dabei automatisch Plan-Admin.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  type="button"
                  className="bg-[#17c671] hover:bg-[#14a85d] text-white"
                  onClick={handleOpenPlanManagement}
                  data-testid="open-plan-management-btn"
                >
                  Dienstplan erstellen
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-[#212529] flex items-center gap-2">
                <User className="w-5 h-5 text-[#17c671]" />
                Profil
              </CardTitle>
              <CardDescription className="text-gray-500">
                Passe deinen Namen und deine Telefonnummer an.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="profile-email">E-Mail</Label>
                  <Input
                    id="profile-email"
                    type="email"
                    value={user?.email || ""}
                    readOnly
                    disabled
                    className="border-gray-300 bg-gray-100 text-gray-500"
                    data-testid="profile-email-input-readonly"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Name</Label>
                  <Input
                    id="profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="border-gray-300 focus:border-[#17c671]"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="profile-phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Telefonnummer
                  </Label>
                  <Input
                    id="profile-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+49 123 456789"
                    className="border-gray-300 focus:border-[#17c671]"
                    data-testid="profile-phone-input"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="bg-[#17c671] hover:bg-[#14a85d] text-white"
                    disabled={savingProfile}
                    data-testid="profile-save-btn"
                  >
                    {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-[#212529] flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#17c671]" />
                Passwort ändern
              </CardTitle>
              <CardDescription className="text-gray-500">
                Für die Aenderung ist dein aktuelles Passwort notwendig.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Aktuelles Passwort</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="border-gray-300 focus:border-[#17c671]"
                    data-testid="profile-current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Neues Passwort</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="border-gray-300 focus:border-[#17c671]"
                    data-testid="profile-new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Neues Passwort wiederholen</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="border-gray-300 focus:border-[#17c671]"
                    data-testid="profile-confirm-password-input"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    className="bg-[#17c671] hover:bg-[#14a85d] text-white"
                    disabled={savingPassword}
                    data-testid="profile-password-save-btn"
                  >
                    {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                    Passwort speichern
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <AppFooter />
    </div>
  );
};

export default UserSettingsPage;
